import listener from '@mailobj-browser/front/js/events/listeners/listener.js'
import passive from '@mailobj-browser/front/js/events/options/passive.js'
import one from '@mailobj-browser/front/js/selectors/one.js'
import object from '@mailobj-browser/front/js/utils/object.js'
import * as utilities from '#utilities/js/styles.js'
import { elements, modifiers } from '../../styles.js'
import attachVideo from './attachvideo.js'
import { setOrganizer, scopeProps, setState, states } from './props.js'
import { ctrlButton, displayVideos, meetBlock } from './ui.js'

let initialAttendees = []
const attendees = new Map()
const sessions = new Set()
const videos = new Map()

export const reset = () => {
  initialAttendees = []
  attendees.clear()
  sessions.clear()
  videos.clear()
}

const uiProps = (videoEltId, parentNode) => {
  const video = one(`#${videoEltId}`, parentNode)
  if (!video) {
    return null
  }
  const participant = video.closest(`.${elements.meet_camera}`)
  const name = one(`.${elements.camera_participant_name} span`, video.parentNode)
  const iconMute = one(`.${elements.camera_participant_name} .${utilities.elements.icon}`, video.parentNode)
  return { video, participant, name, iconMute }
}

const type = 'EA_VIDEOPROPS'
const onVideoProps = object(listener, {
  type,
  passive,
  task: (
    element,
    event
  ) => {
    const { detail } = event
    const {
      videoProps = [],
      isMyConference = false,
      registerOnly = false,
      status = '',
      initialAttendees: initAttendees = []
    } = detail
    const { caller, dial_attendees_id, myself } = scopeProps()

    if (status === 'connected') {
      initialAttendees = initAttendees
      setState(states.connect)
      displayVideos()
      return
    }

    /* on vérifie que les participants initiaux sont toujours là */
    const videoNames = videoProps.map(p => p.videoName)
    for (let i = initialAttendees.length - 1; i >= 0; i--) {
      const attendee = initialAttendees[i]
      /* le cas échéant, on les supprime de la liste */
      if (!videoNames.includes(attendee))
        initialAttendees.splice(i, 1)
    }
    /* il y a eu des participants avant nous, on se replace */
    if (initialAttendees.length > 0) {
      const myself = videoProps.splice(0, 1)[0]
      videoProps.splice(initialAttendees.length, 0, myself)
    }

    let iAmMute = false
    let organizerFound = false
    /* populate name */
    let videoIdx = 1
    for (const prop of videoProps) {
      prop.videoEltId = 'videoGallery_' + videoIdx++
      const name = dial_attendees_id[prop.videoName]
      if (name) {
        prop.name = name
      } else {
        prop.name = myself
        iAmMute = !!prop.mute
      }
      if (prop.organizer) {
        organizerFound = true
        setOrganizer(prop.name)
      }
    }

    const muteButton = ctrlButton('mute')
    const unmuteButton = ctrlButton('unmute')
    if (iAmMute) {
      muteButton.classList.add(utilities.modifiers.display.none)
      unmuteButton.classList.remove(utilities.modifiers.display.none)
    } else {
      unmuteButton.classList.add(utilities.modifiers.display.none)
      muteButton.classList.remove(utilities.modifiers.display.none)
    }
    const block = meetBlock()
    if (isMyConference) {
      block.classList.add(elements.meet_organizer_enabled)
    } else {
      block.classList.remove(elements.meet_organizer_enabled)
    }

    /* UI update for connected participants */
    for (const prop of videoProps) {
      const ui = uiProps(prop.videoEltId, element)
      if (!ui) {
        return
      }
      const { video, participant, name, iconMute } = ui
      const cameraParticipantName = one(`.${elements.camera_participant_name}`, participant)

      // name
      name.textContent = prop.name
      if (prop.name === myself) {
        participant.classList.add(modifiers.participant.myself)
      } else {
        participant.classList.remove(modifiers.participant.myself)
      }

      // talking status
      if (prop.talking) {
        cameraParticipantName.classList.add(modifiers.participant_name.talking)
      } else {
        cameraParticipantName.classList.remove(modifiers.participant_name.talking)
      }

      // mute status
      if (prop.mute) {
        iconMute.classList.remove(utilities.modifiers.display.none)
      } else {
        iconMute.classList.add(utilities.modifiers.display.none)
      }

      // is organizer ?
      if ((!organizerFound && prop.name === caller)
        || (organizerFound && (prop.organizer || (prop.name === myself && isMyConference)))) {
        participant.classList.add(modifiers.participant.isOrganizer)
      } else {
        participant.classList.remove(modifiers.participant.isOrganizer)
      }

      // video
      if (prop.name !== myself)
        video.muted = prop.mute
      participant.classList.remove(modifiers.participant.notConnected)
      if (prop.videoSession) {
        if (prop.registerSession) {
          sessions.add(prop.name)
        }
        if (prop.name === myself || sessions.has(prop.name)) {
          participant.classList.remove(modifiers.participant.hidden)
          attachVideo(prop.videoEltId, prop.videoSession)
          videos.set(video, prop.videoName)
        }
        const { videoWidth, videoHeight } = video
        if (videoWidth && videoHeight) {
          if (videoWidth > videoHeight) {
            video.classList.remove(modifiers.participant.portrait)
            video.classList.add(modifiers.participant.landscape)
          } else {
            video.classList.remove(modifiers.participant.landscape)
            video.classList.add(modifiers.participant.portrait)
          }
        }
      } else {
        sessions.delete(prop.name)
        video.classList.remove(modifiers.participant.portrait)
        video.classList.remove(modifiers.participant.landscape)
        participant.classList.add(modifiers.participant.hidden)
      }
    }

    /* UI update for NOT connected participants */
    const allAttendees = [myself, ...Object.values(dial_attendees_id)]
    const connectedAttendees = videoProps.map((prop) => prop.name)
    const notConnectedAttendees = allAttendees.filter(email => !connectedAttendees.includes(email))

    for (const email of notConnectedAttendees) {
      const videoId = 'videoGallery_' + videoIdx++
      const ui = uiProps(videoId, element)
      if (!ui) {
        return
      }
      const { participant, name, iconMute } = ui
      const cameraParticipantName = one(`.${elements.camera_participant_name}`, participant)

      participant.classList.add(modifiers.participant.notConnected)
      name.textContent = email
      cameraParticipantName.classList.remove(modifiers.participant_name.talking)
      iconMute.classList.add(utilities.modifiers.display.none)
    }
  }
})

export const getName = (video) => {
  return videos.get(video)
}

export default element => {
  onVideoProps.listen(element)
}
